/**
 * orgMembers — ONE reading of "who is in this team / department".
 *
 * The two org detail pages each carried the same two things, written twice:
 *
 *   1. a member → collaborator mapping, inline in JSX inside a Button's
 *      `onClick`, thirty lines deep, so the payload the manage side panel got
 *      was authored in a place nobody would look for it; and
 *   2. a hand-rolled read-only list beside it (avatar, name, email, a Lead
 *      badge) which was a fifth copy of the row anatomy `AccessList` owns.
 *
 * (2) is gone — the shared `AccessPane` renders the list now. (1) lives here,
 * once, so a team and a department normalise a member the same way.
 *
 * THE LEAD IS THE OWNER ROW. `accessModel` pins whoever is the owner or the
 * lead to the top of an access list and badges them with `ownerLabel`, and it
 * derives `isOwner` from the access level. A raw team member carries neither
 * an access field nor `is_lead` — only `role: 'lead' | 'member'` — so without
 * this mapping the lead would sort in wherever the backend happened to put
 * them and read as a plain viewer.
 */

import type { ReactNode } from 'react';

/** The one place a raw org member turns into an access row. */
export const toAccessRows = (members: any[] | null | undefined): any[] =>
  (members || []).map((m: any) => ({
    user_id: m.id,
    collaborator_uuid: m.uuid || String(m.id ?? ''),
    name: m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.email || '',
    email: m.email || '',
    initials:
      m.initials ||
      ((m.first_name?.[0] || '') + (m.last_name?.[0] || '')).toUpperCase() ||
      '??',
    profile_picture: m.profile_picture || null,
    // Teams and departments have no per-member access levels; the ROLE is the
    // only signal, and it is what the manage panel has always been handed.
    access: m.role === 'lead' ? 'admin' : m.role === 'member' ? 'edit' : 'view',
    role: m.role,
    // `normalise` pins on `is_lead`; the role alone would not reach it.
    is_lead: m.role === 'lead',
    source: 'direct',
  }));

export interface OrgAccessPanelOptions {
  /** The team or department uuid. */
  resourceUuid: string;
  resourceType: 'team' | 'department';
  members: any[] | null | undefined;
  /** Already translated. */
  title: string;
  subMsg: string;
  icon: ReactNode;
  currentUserUuid?: string;
  addMember: (resourceUuid: string, userUuid: string) => Promise<unknown>;
  removeMember: (resourceUuid: string, memberUuid: string) => Promise<unknown>;
  /** Reload the record once the panel has committed its changes. */
  onUpdate: () => void;
}

/**
 * The `manageResourceCollaborators` side-panel payload for an org record.
 *
 * `updateAccess` is a no-op that reports success: the panel offers a level
 * dropdown for every resource, and neither teams nor departments store one.
 * Rejecting the change would surface an error for something the user cannot
 * do anything about; the levels shown are derived from the role instead.
 */
export const orgAccessPanelPayload = ({
  resourceUuid,
  resourceType,
  members,
  title,
  subMsg,
  icon,
  currentUserUuid,
  addMember,
  removeMember,
  onUpdate,
}: OrgAccessPanelOptions): Record<string, unknown> => ({
  type: 'manageResourceCollaborators',
  showSidePanel: true,
  size: 'md',
  message: title,
  subMsg,
  icon,
  resourceUuid,
  resourceType,
  collaborators: toAccessRows(members),
  currentUserUuid,
  canAdmin: true,
  addCollaborator: async (uuid: string, userUuid: string) => {
    await addMember(uuid, userUuid);
  },
  removeCollaborator: async (uuid: string, collabUuid: string) => {
    await removeMember(uuid, collabUuid);
  },
  updateAccess: async () => true,
  onUpdate,
});
